// CareLogix™ ORACLE — OracleSignalReplay (Phase 33 §2)
// Historical signal timeline replay (deterministic) with play/pause.
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";

function seed(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const CONVICTIONS = ["watch_strong", "watch", "consider", "hold", "avoid"];

function buildHistory(symbol, count = 12) {
  const out = [];
  for (let i = 0; i < count; i += 1) {
    const k = `${symbol}-${i}`;
    const opp = seed(k + "opp") % 100;
    const risk = seed(k + "risk") % 100;
    const conf = seed(k + "conf") % 100;
    const conviction = CONVICTIONS[seed(k + "con") % CONVICTIONS.length];
    const days = count - i;
    out.push({
      step: i + 1,
      label: `T-${days * 4}h`,
      conviction,
      opportunity_score: opp,
      risk_score: risk,
      confidence_score: conf,
    });
  }
  return out;
}

export default function OracleSignalReplay({ symbol = "AAPL", testid = "oracle-signal-replay" }) {
  const [history] = useState(() => buildHistory(symbol, 12));
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) { if (timer.current) clearInterval(timer.current); return; }
    timer.current = setInterval(() => {
      setIdx((i) => (i + 1) % history.length);
    }, 1200);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [playing, history.length]);

  const cur = history[idx];

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>ORACLE SIGNAL REPLAY™</div>
          <h3 style={S.title}>{symbol} · Historical Conviction Timeline</h3>
        </div>
        <div style={S.controls}>
          <button type="button" onClick={() => setPlaying((v) => !v)} style={S.btn} data-testid={`${testid}-play`}>{playing ? "⏸ Pause" : "▶ Play"}</button>
          <button type="button" onClick={() => setIdx(0)} style={S.btn} data-testid={`${testid}-restart`}>⟳ Restart</button>
        </div>
      </header>
      <div style={S.track}>
        {history.map((h, i) => (
          <button
            key={h.step}
            type="button"
            onClick={() => { setIdx(i); setPlaying(false); }}
            style={{ ...S.tick, background: i === idx ? PALETTE.gold : "rgba(255,255,255,0.10)" }}
            aria-label={`Replay step ${h.step}`}
            data-testid={`${testid}-tick-${i}`}
          />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={cur.step}
          style={S.frame}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.3 }}
          data-testid={`${testid}-frame`}
        >
          <div style={S.frameHead}>
            <span style={S.frameLabel}>{cur.label}</span>
            <span style={{ ...S.conv, color: PALETTE.gold }}>{cur.conviction.replace("_", " ").toUpperCase()}</span>
          </div>
          <div style={S.scoreGrid}>
            {[
              { k: "Opportunity", v: cur.opportunity_score },
              { k: "Risk",        v: cur.risk_score },
              { k: "Confidence",  v: cur.confidence_score },
            ].map((m) => (
              <div key={m.k} style={S.scoreCell}>
                <div style={S.scoreHead}>
                  <span style={S.scoreLbl}>{m.k}</span>
                  <span style={{ ...S.scoreVal, color: scoreColor(m.v) }}>{m.v}</span>
                </div>
                <div style={S.bar}><div style={{ ...S.barFill, width: `${m.v}%`, background: scoreColor(m.v) }} /></div>
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      <footer style={S.foot}>Replays of historical convictions. Past performance is not predictive.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 16, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 10 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 16 },
  controls: { display: "flex", gap: 6 },
  btn:      { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "5px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  track:    { display: "flex", gap: 4 },
  tick:     { width: 18, height: 6, borderRadius: 3, border: "none", cursor: "pointer" },
  frame:    { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  frameHead:{ display: "flex", justifyContent: "space-between", alignItems: "center" },
  frameLabel:{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: PALETTE.slate4 },
  conv:     { fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: "0.10em", fontWeight: 800 },
  scoreGrid:{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 },
  scoreCell:{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: 8 },
  scoreHead:{ display: "flex", justifyContent: "space-between", alignItems: "center" },
  scoreLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.06em" },
  scoreVal: { fontFamily: "'Space Mono', monospace", fontSize: 13, fontWeight: 800 },
  bar:      { marginTop: 6, height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  barFill:  { height: "100%" },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
